import { isCreatorPagesMode } from "../componentsTable";
import { supabase } from "../../config/supabaseClient";
import { markLinkInBioContentEdited } from "../../Screens/DashboardView/GettingStarted/gettingStartedProgress";
import {
  getComponentsTableName,
  getUserLinksTypeForSurface,
  WEBSITE_TEMPLATE_SURFACE,
} from "../StayUpToDate/componentSurface";
import type {
  LinkImageCropByLayout,
  PersistedImageCrop,
} from "../imageCrop/cropStatePersist";
import { isMissingImageCropColumnError } from "../imageCrop/cropStatePersist";

type UserLinkImageCropPayload =
  | PersistedImageCrop
  | LinkImageCropByLayout
  | null;

/**
 * Lean columns for My Links canvas + edit drawer.
 * Avoid `select("*")` — large `image_crop` payloads stall Safari on open.
 */
export const USER_LINKS_LIST_COLUMNS =
  "id, link_name, link_url, image_url, original_image_url, image_crop, index, type, parent_id, collection_name";

function maybeMarkLibFromUserLinks(
  from: unknown,
  userId: unknown,
) {
  void markLinkInBioContentEdited(
    userId == null ? null : String(userId),
    null,
    from == null ? null : String(from),
  );
}

function shouldScopeLinksByParentId(from?: string, componentId?: string | null) {
  const id = String(componentId || "").trim();
  if (!id) return false;
  if (from === WEBSITE_TEMPLATE_SURFACE) return true;
  if (from === "landing-page") return true;
  return isCreatorPagesMode(from);
}

function applyUserLinksScope(
  query: any,
  {
    user_id,
    collectionName,
    from,
    componentId,
  }: {
    user_id: any;
    collectionName?: any;
    from?: any;
    componentId?: string | null;
  }
) {
  let scopedQuery = query.eq("user_id", user_id);

  if (shouldScopeLinksByParentId(from, componentId)) {
    return scopedQuery.eq("parent_id", componentId);
  }

  if (collectionName !== undefined) {
    scopedQuery = scopedQuery.eq("collection_name", collectionName);
  }

  if (from !== undefined) {
    scopedQuery = scopedQuery.eq("type", from);
  }

  return scopedQuery;
}

function userLinksTypeFor(from?: unknown) {
  return getUserLinksTypeForSurface(
    from == null ? null : String(from),
  );
}

/** Oldest active links-section row for a display title — owns legacy title-keyed rows. */
async function isOldestLinksSectionForTitle({
  user_id,
  collectionName,
  from,
  componentId,
}: {
  user_id: string;
  collectionName: string;
  from?: string;
  componentId: string;
}): Promise<boolean> {
  const componentsTable = getComponentsTableName(from);
  const { data, error } = await supabase
    .from(componentsTable)
    .select("id")
    .eq("user_id", user_id)
    .eq("name", collectionName)
    .eq("type", "links-section")
    .neq("status", "deleted")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const oldestId = String(data?.id || "").trim();
  return Boolean(oldestId) && oldestId === componentId;
}

/** Bind legacy title-keyed rows to a component id (oldest section only). */
export async function claimLegacyTitleLinksForComponent({
  user_id,
  collectionName,
  from,
  componentId,
}: {
  user_id: string;
  collectionName: string;
  from?: string;
  componentId: string;
}): Promise<any[]> {
  const uid = String(user_id || "").trim();
  const title = String(collectionName || "").trim();
  const boundId = String(componentId || "").trim();
  if (!uid || !title || !boundId) return [];

  const linksType = userLinksTypeFor(from);
  const ownsLegacy = await isOldestLinksSectionForTitle({
    user_id: uid,
    collectionName: title,
    from,
    componentId: boundId,
  });
  if (!ownsLegacy) return [];

  const { data: legacyLinks, error: legacyLinksError } = await supabase
    .from("user_links")
    .select(USER_LINKS_LIST_COLUMNS)
    .eq("collection_name", title)
    .eq("user_id", uid)
    .eq("type", linksType)
    .is("parent_id", null)
    .eq("status", "active")
    .order("index");
  if (legacyLinksError) throw legacyLinksError;
  if (!legacyLinks?.length) return [];

  const legacyLinkIds = legacyLinks.map((link: { id?: unknown }) => link.id).filter(Boolean);
  if (legacyLinkIds.length) {
    const { error: claimErr } = await supabase
      .from("user_links")
      .update({ parent_id: boundId })
      .in("id", legacyLinkIds);
    if (claimErr) throw claimErr;
  }

  return legacyLinks.map((link: any) => ({ ...link, parent_id: boundId }));
}

/** Load active links for a bound links-section (parent_id first, legacy claim fallback). */
export async function fetchUserLinksForBoundComponent({
  user_id,
  collectionName,
  from,
  componentId,
}: {
  user_id: string;
  collectionName: string;
  from?: string;
  componentId: string;
}) {
  const uid = String(user_id || "").trim();
  const boundId = String(componentId || "").trim();
  if (!uid || !boundId) return [];

  const { data: byParent, error: byParentErr } = await supabase
    .from("user_links")
    .select(USER_LINKS_LIST_COLUMNS)
    .eq("user_id", uid)
    .eq("parent_id", boundId)
    .eq("status", "active")
    .order("index", { ascending: true });
  if (byParentErr) throw byParentErr;
  if (byParent?.length) return byParent;

  // Only when nothing is bound yet — skip on the hot path for already-migrated sections.
  const title = String(collectionName || "").trim();
  if (!title) return [];

  return claimLegacyTitleLinksForComponent({
    user_id: uid,
    collectionName: title,
    from,
    componentId: boundId,
  });
}

/** Soft-delete every link row owned by a links-section component. */
export async function softDeleteUserLinksForLinksSection({
  user_id,
  componentId,
  collectionName,
  from,
}: {
  user_id: string;
  componentId: string;
  collectionName?: string;
  from?: string;
}) {
  const uid = String(user_id || "").trim();
  const boundId = String(componentId || "").trim();
  if (!uid || !boundId) return;

  const linksType = userLinksTypeFor(from);
  await supabase
    .from("user_links")
    .update({ status: "deleted" })
    .eq("user_id", uid)
    .eq("parent_id", boundId);

  const title = String(collectionName || "").trim();
  if (
    title &&
    (await isOldestLinksSectionForTitle({
      user_id: uid,
      collectionName: title,
      from,
      componentId: boundId,
    }))
  ) {
    await supabase
      .from("user_links")
      .update({ status: "deleted" })
      .eq("user_id", uid)
      .eq("type", linksType)
      .eq("collection_name", title)
      .is("parent_id", null);
  }
}

const trimLinkField = (value: any) =>
  typeof value === "string" ? value.trim() : value;

export const LINK_TITLE_ALREADY_EXISTS = "A link with this title already exists";

async function userLinkTitleTaken({
  user_id,
  collectionName,
  from,
  componentId,
  linkName,
  exceptId,
}: {
  user_id: any;
  collectionName?: any;
  from?: any;
  componentId?: string | null;
  linkName: string;
  exceptId?: number | string | null;
}): Promise<boolean> {
  const wanted = String(linkName || "").trim().toLowerCase();
  if (!wanted) return false;
  const { data, error } = await applyUserLinksScope(
    supabase.from("user_links").select("id, link_name"),
    { user_id, collectionName, from, componentId },
  ).eq("status", "active");
  if (error) throw error;
  const except = exceptId == null ? null : String(exceptId);
  return (data || []).some(
    (row: { id?: unknown; link_name?: unknown }) =>
      (except == null || String(row.id) !== except) &&
      String(row.link_name || "").trim().toLowerCase() === wanted,
  );
}

function parseLinkIndex(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Next append index: max(existing index) + 1, or 0 when none. */
export function computeNextLinkIndex(
  links: Array<{ index?: unknown }> | null | undefined
): number {
  if (!links?.length) {
    return 0;
  }

  const indices = links
    .map((link) => parseLinkIndex(link.index))
    .filter((index): index is number => index !== null);

  if (!indices.length) {
    return 0;
  }

  return Math.max(...indices) + 1;
}

/**
 * Rows used only to pick the next `index`. Includes soft-deleted links so a
 * deleted row at index N does not cause a new insert to reuse N and fail.
 */
async function fetchLinksForIndexComputation({
  user_id,
  collectionName,
  from,
  componentId,
}: {
  user_id: any;
  collectionName?: any;
  from?: any;
  componentId?: string | null;
}) {
  if (shouldScopeLinksByParentId(from, componentId)) {
    const [byParent, byLegacy] = await Promise.all([
      supabase
        .from("user_links")
        .select("index")
        .eq("user_id", user_id)
        .eq("parent_id", componentId),
      supabase
        .from("user_links")
        .select("index")
        .eq("user_id", user_id)
        .eq("collection_name", collectionName)
        .eq("type", from)
        .is("parent_id", null),
    ]);

    if (byParent.error) {
      throw byParent.error;
    }
    if (byLegacy.error) {
      throw byLegacy.error;
    }

    return [...(byParent.data || []), ...(byLegacy.data || [])];
  }

  const { data, error } = await applyUserLinksScope(
    supabase.from("user_links").select("index"),
    {
      user_id,
      collectionName,
      from,
      componentId,
    }
  );

  if (error) {
    throw error;
  }

  return data || [];
}

export async function storeUserLinks(
  id: any,
  user_id: any,
  username: any,
  linkName: any,
  link: any,
  imageLinkUrl: any,
  collectionName: any,
  from: any,
  componentId?: string | null,
  originalImageUrl?: string | null,
  imageCrop?: UserLinkImageCropPayload
) {
  linkName = trimLinkField(linkName);
  link = trimLinkField(link);
  const scopeByParentId = shouldScopeLinksByParentId(from, componentId);
  // Only write when caller passes a value (omit = leave column alone).
  const originalImageFields =
    originalImageUrl === undefined
      ? {}
      : { original_image_url: originalImageUrl || null };
  const imageCropFields =
    imageCrop === undefined ? {} : { image_crop: imageCrop };

  // If id is null, this is a new record
  if (id === null) {
    const existingLinksTotal = await fetchLinksForIndexComputation({
      user_id,
      collectionName,
      from,
      componentId,
    });

    const newIndex = computeNextLinkIndex(existingLinksTotal);

    if (
      await userLinkTitleTaken({
        user_id,
        collectionName,
        from,
        componentId,
        linkName,
      })
    ) {
      throw new Error(LINK_TITLE_ALREADY_EXISTS);
    }

    const insertPayload = {
      status: "active",
      user_id,
      link_name: linkName,
      link_url: link,
      image_url: imageLinkUrl,
      ...originalImageFields,
      ...imageCropFields,
      collection_name: collectionName,
      type: userLinksTypeFor(from),
      index: newIndex,
      ...(scopeByParentId ? { parent_id: componentId } : {}),
    };

    let { data, error } = await supabase
      .from("user_links")
      .insert(insertPayload)
      .select();

    if (error && isMissingImageCropColumnError(error) && "image_crop" in insertPayload) {
      const { image_crop: _omit, ...withoutCrop } = insertPayload;
      ({ data, error } = await supabase
        .from("user_links")
        .insert(withoutCrop)
        .select());
    }

    if (error) {
      console.error("Error storing new link:", error);
      throw error;
    }

    maybeMarkLibFromUserLinks(from, user_id);
    return data;
  } else {
    // Update existing record
    if (
      await userLinkTitleTaken({
        user_id,
        collectionName,
        from,
        componentId,
        linkName,
        exceptId: id,
      })
    ) {
      throw new Error(LINK_TITLE_ALREADY_EXISTS);
    }

    const updatePayload = {
      link_name: linkName,
      link_url: link,
      image_url: imageLinkUrl,
      ...originalImageFields,
      ...imageCropFields,
      collection_name: collectionName,
      ...(scopeByParentId ? { parent_id: componentId } : {}),
    };

    // Row updates always target the primary key — legacy rows may lack parent_id.
    let { data, error } = await supabase
      .from("user_links")
      .update(updatePayload)
      .eq("user_id", user_id)
      .eq("id", id)
      .eq("status", "active")
      .select();

    if (error && isMissingImageCropColumnError(error) && "image_crop" in updatePayload) {
      const { image_crop: _omit, ...withoutCrop } = updatePayload;
      ({ data, error } = await supabase
        .from("user_links")
        .update(withoutCrop)
        .eq("user_id", user_id)
        .eq("id", id)
        .eq("status", "active")
        .select());
    }

    if (error) {
      console.error("Error updating link:", error);
      throw error;
    }

    maybeMarkLibFromUserLinks(from, user_id);
    return data;
  }
}

export async function deleteUserLinks(
  user_id: any,
  collectionName: any,
  id: any,
  from?: any,
  componentId?: string | null
) {
  void collectionName;
  void from;
  void componentId;
  await supabase
    .from("user_links")
    .update({ status: "deleted" })
    .eq("user_id", user_id)
    .eq("id", id);

  maybeMarkLibFromUserLinks(from, user_id);
}

export async function updateUserLinksCollectionName(
  component: any,
  name: any,
  user_id: any,
  from?: any
) {
  name = trimLinkField(name);
  const componentId = String(component?.id || "").trim();
  let request = supabase
    .from("user_links")
    .update({
      collection_name: name,
    })
    .eq("user_id", user_id)
    .eq("status", "active");
  if (shouldScopeLinksByParentId(from, componentId)) {
    request = request.eq("parent_id", componentId);
  } else {
    request = request.eq("collection_name", component?.name);
  }
  await request;

  maybeMarkLibFromUserLinks(from ?? "landing-page", user_id);
}


export async function UpdateUserLinksIndex(
  newOrder: any,
  user_id: string,
  name: string,
  from?: any,
  componentId?: string | null
) {
  const rows = Array.isArray(newOrder) ? newOrder : [];
  // Parallel writes (callers often remap `index` before calling, so we cannot
  // skip by comparing link.index — always persist position from array order).
  await Promise.all(
    rows.map((link: { id?: unknown }, idx: number) => {
      if (link == null || link.id == null) return Promise.resolve();
      return applyUserLinksScope(
        supabase.from("user_links").update({ index: idx }),
        {
          user_id,
          collectionName: name,
          from,
          componentId,
        },
      )
        .eq("id", link.id)
        .eq("status", "active");
    }),
  );

  maybeMarkLibFromUserLinks(from, user_id);
}





export async function storeUserLinksBulk(
  id: any,
  user_id: any,
  username: any,
  linkName: any,
  link: any,
  imageLinkUrl: any,
  collectionName: any,
  from: any,
  componentId?: string | null
) {
  const scopeByParentId = shouldScopeLinksByParentId(from, componentId);
  // If id is null, this is a new record
  if (id === null) {
    const existingLinksTotal = await fetchLinksForIndexComputation({
      user_id,
      collectionName,
      from,
      componentId,
    });

    const newIndex = computeNextLinkIndex(existingLinksTotal);

    const { data: duplicateCheck, error: dupError } =
      await applyUserLinksScope(
        supabase.from("user_links").select("id"),
        {
          user_id,
          collectionName,
          from,
          componentId,
        }
      )
        .eq("link_name", linkName)
        .eq("status", "active");
  
  if (dupError) {
    throw dupError;
  }

  if (duplicateCheck && duplicateCheck.length > 0) {
    return;
  }
    const { data, error } = await supabase
      .from("user_links")
      .insert({
        user_id,
        link_name: linkName,
        link_url: link,
        image_url: imageLinkUrl,
        collection_name: collectionName,
        type: userLinksTypeFor(from),
        index: newIndex,
        ...(scopeByParentId ? { parent_id: componentId } : {}),
      })
      .select();

    if (error) {
      throw error;
    }

    maybeMarkLibFromUserLinks(from, user_id);
    return data;
  } else {
    // Update existing record
    const { data, error } = await supabase
      .from("user_links")
      .update({
        link_name: linkName,
        link_url: link,
        image_url: imageLinkUrl,
        collection_name: collectionName,
        ...(scopeByParentId ? { parent_id: componentId } : {}),
      })
      .eq("user_id", user_id)
      .eq("id", id)
      .eq("status", "active")
      .select();

    if (error) {
      throw error;
    }

    maybeMarkLibFromUserLinks(from, user_id);
    return data;
  }
}
